import type { EffectiveFeature, FeatureKey } from '@cdf/api-client-react';

export const FEATURE_GROUPS: ReadonlyArray<{ key: FeatureKey; label: string; description: string }> = [
  { key: 'booking', label: 'Booking', description: 'Reservations, series and itineraries.' },
  { key: 'workplace_planning', label: 'My work plan', description: 'Personal work plans.' },
  { key: 'teams_and_colleagues', label: 'Teams and colleagues', description: 'Teams, colleague discovery and team attendance.' },
  { key: 'visitors', label: 'Visitors', description: 'Visits, guest reservations and front desk.' },
  { key: 'staffed_services', label: 'Staffed services', description: 'Team-delivered service requests.' },
  { key: 'maps_rooms', label: 'Maps and room browsing', description: 'Floor maps and room discovery.' },
  { key: 'reporting', label: 'Reporting', description: 'Dashboards, reports and team attendance.' },
  { key: 'ai_tools', label: 'AI tools', description: 'Assistant and AI insights.' }
];

export function featureLabel(key: FeatureKey): string {
  return FEATURE_GROUPS.find((group) => group.key === key)?.label ?? key;
}

export function featureAvailable(features: readonly EffectiveFeature[] | undefined, key: FeatureKey): boolean {
  return features?.find((feature) => feature.moduleKey === key)?.effectiveEnabled === true;
}

export function featureRestriction(feature: EffectiveFeature): string | null {
  if (feature.blockedBy === 'platform') return 'The platform setting for this parent tenant is off.';
  if (feature.blockedBy === 'tenant') return 'The parent tenant setting is off.';
  if (feature.blockedBy === 'entity') return 'This entity opted out. Reset to inherit to follow the parent tenant.';
  if (!feature.ready) return 'This feature is not ready for this entity yet.';
  return null;
}

/** Keep unrelated navigation during refresh; visitor destinations require current admission. */
export function navigationFeatureState(query: {
  data?: { items: readonly EffectiveFeature[] };
  isError: boolean;
  isFetching: boolean;
}): readonly EffectiveFeature[] | undefined {
  const features = query.isError ? undefined : query.data?.items;
  return query.isFetching && features?.some(feature => feature.moduleKey === 'visitors' || feature.moduleKey === 'teams_and_colleagues')
    ? features.filter(feature => feature.moduleKey !== 'visitors' && feature.moduleKey !== 'teams_and_colleagues') : features;
}

/** Route gates retain the last resolved state during a background refresh. */
export function routeFeatureStatus(query: {
  tenantSlug: string | null;
  data?: { items: readonly EffectiveFeature[] };
  isPending: boolean;
  isError: boolean;
}, key: FeatureKey): 'enabled' | 'disabled' | 'loading' | 'error' {
  if (!query.tenantSlug) return 'enabled';
  if (query.isPending) return 'loading';
  if (query.isError || !query.data) return 'error';
  return featureAvailable(query.data.items, key) ? 'enabled' : 'disabled';
}

/** Select menu destinations only; route mounting and history access are independent. */
export function filterRoutesByFeature<T extends { requiredFeature?: FeatureKey; additionalFeature?: FeatureKey }>(routes: readonly T[], features: readonly EffectiveFeature[] | undefined): T[] {
  return routes.filter((route) =>
    (!route.requiredFeature || featureAvailable(features, route.requiredFeature)) &&
    (!route.additionalFeature || featureAvailable(features, route.additionalFeature))
  );
}

export function featureErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'code' in error && error.code === 'FEATURE_DISABLED') {
    return 'This feature is off. Existing work can still be viewed and completed where permitted.';
  }
  return error instanceof Error ? error.message : 'The request could not be completed.';
}
